import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Loader2 } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useCart } from '../context/CartContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [error, setError] = useState(null);
  const { clearCart } = useCart();

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setError('Nedostaje oznaka plaćanja. Obratite nam se za provjeru narudžbe.');
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    let timer;
    let attempts = 0;
    setLoading(true);
    setError(null);
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/checkout-status/${encodeURIComponent(sessionId)}`, { signal: controller.signal, timeout: 20000 });
        if (controller.signal.aborted) return;
        if (data.payment_status === 'paid') {
          setPaymentStatus(data);
          clearCart();
          setLoading(false);
        } else if (data.status === 'expired') {
          setError('Sesija plaćanja je istekla. Molimo pokušajte ponovo.');
          setLoading(false);
        } else if (++attempts >= 10) {
          setError('Plaćanje još nije potvrđeno. Obratite nam se za provjeru narudžbe.');
          setLoading(false);
        } else {
          timer = setTimeout(poll, 2000);
        }
      } catch (err) {
        if (!axios.isCancel(err)) {
          setError('Greška pri provjeri statusa plaćanja. Obratite nam se prije ponovnog plaćanja.');
          setLoading(false);
        }
      }
    };
    // Defer the initial call so StrictMode's setup/cleanup does not send an extra request.
    timer = setTimeout(poll, 0);
    return () => { controller.abort(); clearTimeout(timer); };
  }, [sessionId, clearCart]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-gray-600" />
          <p className="text-gray-600" role="status" data-testid="payment-status-loading">Provjeravam status plaćanja...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12">
        <div className="max-w-xl mx-auto px-4">
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-2xl text-red-600">Greška</CardTitle>
              <CardDescription role="alert" data-testid="payment-status-error">{error}</CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <Button onClick={() => navigate('/')} size="lg" data-testid="payment-error-home-button">
                Povratak na početnu
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle className="text-3xl" data-testid="payment-success-title">Plaćanje uspješno!</CardTitle>
            <CardDescription className="text-lg">
              Hvala vam na kupovini
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {paymentStatus && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4">Detalji plaćanja</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className="font-semibold text-green-600" data-testid="payment-confirmed-status">Plaćeno</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Iznos:</span>
                    <span className="font-semibold" data-testid="payment-confirmed-amount">{paymentStatus.amount_total.toFixed(2)} {paymentStatus.currency.toUpperCase()}</span>
                  </div>
                  {paymentStatus.metadata?.order_number && (
                    <div className="flex flex-wrap justify-between gap-2">
                      <span className="text-gray-600">Broj narudžbe:</span>
                      <span className="font-semibold break-all" data-testid="payment-confirmed-order-number">{paymentStatus.metadata.order_number}</span>
                    </div>
                  )}
                  {paymentStatus.metadata?.customer_email && (
                    <div className="flex flex-wrap justify-between gap-2">
                      <span className="text-gray-600">Email:</span>
                      <span className="font-semibold break-all" data-testid="payment-contact-email">{paymentStatus.metadata.customer_email}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="text-center space-y-4">
              <p className="text-gray-600" data-testid="payment-confirmation-note">
                Vaša uplata je potvrđena, a narudžba zaprimljena.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button onClick={() => navigate('/')} size="lg" data-testid="payment-success-home-button">
                  Povratak na početnu
                </Button>
                <Button onClick={() => navigate('/webshop')} variant="outline" size="lg" data-testid="payment-success-shopping-button">
                  Nastavi kupovinu
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OrderSuccess;
